import {isFiniteNumber} from '../utils/numberUtils';
import * as Settings from '../utils/Settings';

const INITIAL_VALUE = 0;

/**
 * This service is responsible for deciding whether a quality change event should be sent or not, based on
 *  - the quality changes count that has already been sent in a certain timeframe/interval
 *  - the previous video and audio bitrate values, to avoid sending the first quality change event or the following
 *    events when the bitrate did not change (some players report quality changes with the same bitrate, which should
 *    not be tracked)
 *  - whether the startup has finished, to avoid sending quality change events for the initial bitrate values
 *    that some players report before startup, which can differ from the ones that are used after startup
 */
export default class QualityChangeService {
  /** Flag to block quality change events when a certain threshold in a certain timeframe/interval is exceeded */
  private qualityChangeCount = INITIAL_VALUE;
  private qualityChangeCountResetIntervalId: ReturnType<typeof setInterval> | undefined;
  private readonly QUALITY_CHANGE_COUNT_THRESHOLD: number;

  /** Flag to block the first (initial) video quality change event or the following events when the bitrate did not change */
  private previousVideoBitrate = INITIAL_VALUE;
  /** Flag to block the first (initial) audio quality change event or the following events when the bitrate did not change */
  private previousAudioBitrate = INITIAL_VALUE;

  /** Flag to block quality change events until startup has finished */
  private startupHasFinished = false;

  constructor(qualityChangeCountThreshold: number = Settings.ANALYTICS_QUALITY_CHANGE_COUNT_THRESHOLD) {
    this.QUALITY_CHANGE_COUNT_THRESHOLD = qualityChangeCountThreshold;
  }

  resetValues() {
    this.qualityChangeCount = INITIAL_VALUE;
    this.stopResetInterval();

    this.previousVideoBitrate = INITIAL_VALUE;
    this.previousAudioBitrate = INITIAL_VALUE;

    this.startupHasFinished = false;
  }

  increaseCounter() {
    this.qualityChangeCount++;
  }

  hasExceededQualityChangeThreshold(): boolean {
    return this.qualityChangeCount > this.QUALITY_CHANGE_COUNT_THRESHOLD;
  }

  setStartupHasFinished(startupVideoBitrate: number | undefined, startupAudioBitrate: number | undefined): void {
    this.startupHasFinished = true;

    // check if the startup video bitrate value is known/defined and valid
    if (isFiniteNumber(startupVideoBitrate)) {
      this.previousVideoBitrate = startupVideoBitrate;
    }
    // check if the startup audio bitrate value is known/defined and valid
    if (isFiniteNumber(startupAudioBitrate)) {
      this.previousAudioBitrate = startupAudioBitrate;
    }
  }

  shouldAllowVideoQualityChange(newBitrate: number | undefined): boolean {
    return (
      // if the startup has finished
      this.startupHasFinished &&
      // if the new bitrate value is valid
      isFiniteNumber(newBitrate) &&
      // if the new bitrate is not the first one reported (the initial value is not still set)
      this.previousVideoBitrate !== INITIAL_VALUE &&
      // if the bitrate did change
      this.previousVideoBitrate !== newBitrate &&
      // if the quality change count threshold has not been exceeded
      !this.hasExceededQualityChangeThreshold()
    );
  }

  setVideoBitrate(newBitrate: number): void {
    if (this.startupHasFinished) {
      this.previousVideoBitrate = newBitrate;
    }
  }

  shouldAllowAudioQualityChange(newBitrate: number | undefined): boolean {
    return (
      // if the startup has finished
      this.startupHasFinished &&
      // if the new bitrate value is valid
      isFiniteNumber(newBitrate) &&
      // if the new bitrate is not the first one reported (the initial value is not still set)
      this.previousAudioBitrate !== INITIAL_VALUE &&
      // if the bitrate did change
      this.previousAudioBitrate !== newBitrate &&
      // if the quality change count threshold has not been exceeded
      !this.hasExceededQualityChangeThreshold()
    );
  }

  setAudioBitrate(newBitrate: number): void {
    if (this.startupHasFinished) {
      this.previousAudioBitrate = newBitrate;
    }
  }

  startResetInterval() {
    if (this.qualityChangeCountResetIntervalId !== undefined) {
      return;
    }

    this.qualityChangeCountResetIntervalId = setInterval(() => {
      this.qualityChangeCount = 0;
    }, Settings.ANALYTICS_QUALITY_CHANGE_COUNT_RESET_INTERVAL);
  }

  stopResetInterval() {
    if (this.qualityChangeCountResetIntervalId === undefined) {
      return;
    }

    clearInterval(this.qualityChangeCountResetIntervalId);
    delete this.qualityChangeCountResetIntervalId;
  }
}
